<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from "vue";
import { useStore } from "vuex";
import { useRouter } from "vue-router";
import { useLayout } from "@/layout/composables/layout";
import {
  collection,
  doc,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore";
import { useToast } from "vue-toastification";
import { db } from "@/firebase";

const store = useStore();
const router = useRouter();
const { onMenuToggle } = useLayout();
const toast = useToast();

const outsideClickListener = ref(null);
const topbarMenuActive = ref(false);

const helpDialogVisible = ref(false);
const helpRequests = ref([]);
let unsubscribeHelpRequests = null;

const userProfile = computed(
  () => store.getters["user"].data || store.getters["customUser"] || {}
);
const isUserAuthenticated = computed(() => store.getters["isAuthenticated"]);

const displayName = computed(() => {
  const user = userProfile.value || {};
  const first = user.firstName || "";
  const last = user.lastName || "";
  return `${first} ${last}`.trim() || user.name || "Admin";
});

const openHelpRequests = computed(() =>
  helpRequests.value.filter((request) => request.status === "open")
);

const openHelpCount = computed(() => openHelpRequests.value.length);

const getRequestSummary = (request) => {
  const tableLabel = request.requesterTableName || request.requesterTableId || "Unknown table";
  const nameLabel = request.requesterName || "Unknown user";
  return `${tableLabel} - ${nameLabel} needs help`;
};

const signOut = async () => {
  await store.dispatch("logOut");
  router.push("/");
};

const formatRequestTimestamp = (timestamp) => {
  if (!timestamp || !timestamp.seconds) return "Now";
  const date = new Date(timestamp.seconds * 1000);
  return date.toLocaleTimeString("sv-SE", {
    hour: "2-digit",
    minute: "2-digit",
  });
};

const markHelpResolved = async (requestId) => {
  try {
    await updateDoc(doc(db, "helpRequests", requestId), {
      status: "resolved",
      resolvedAt: serverTimestamp(),
      resolvedBy: displayName.value,
    });
    toast.success("Hj\u00E4lp\u00E4rende markerat som hanterat.");
  } catch (error) {
    toast.error("Kunde inte uppdatera hj\u00E4lp\u00E4rendet.");
  }
};

const subscribeToHelpRequests = () => {
  const helpQuery = query(
    collection(db, "helpRequests"),
    orderBy("createdAt", "desc"),
    limit(50)
  );

  unsubscribeHelpRequests = onSnapshot(helpQuery, (snapshot) => {
    helpRequests.value = snapshot.docs.map((requestDoc) => ({
      id: requestDoc.id,
      ...requestDoc.data(),
    }));
  });
};

onMounted(() => {
  bindOutsideClickListener();
  subscribeToHelpRequests();
});

onBeforeUnmount(() => {
  unbindOutsideClickListener();
  if (unsubscribeHelpRequests) {
    unsubscribeHelpRequests();
  }
});

const onTopBarMenuButton = () => {
  topbarMenuActive.value = !topbarMenuActive.value;
};

const onSettingsClick = () => {
  topbarMenuActive.value = false;
  router.push({ name: "Edit User Info" });
};

const topbarMenuClasses = computed(() => ({
  "layout-topbar-menu-mobile-active": topbarMenuActive.value,
}));

const bindOutsideClickListener = () => {
  if (!outsideClickListener.value) {
    outsideClickListener.value = (event) => {
      if (isOutsideClicked(event)) {
        topbarMenuActive.value = false;
      }
    };
    document.addEventListener("click", outsideClickListener.value);
  }
};

const unbindOutsideClickListener = () => {
  if (outsideClickListener.value) {
    document.removeEventListener("click", outsideClickListener.value);
    outsideClickListener.value = null;
  }
};

const isOutsideClicked = (event) => {
  if (!topbarMenuActive.value) return false;

  const sidebarEl = document.querySelector(".layout-topbar-menu");
  const topbarEl = document.querySelector(".layout-topbar-menu-button");

  return !(
    sidebarEl.isSameNode(event.target) ||
    sidebarEl.contains(event.target) ||
    topbarEl.isSameNode(event.target) ||
    topbarEl.contains(event.target)
  );
};
</script>

<template>
  <div class="layout-topbar">
    <button
      class="p-link layout-menu-button layout-topbar-button"
      @click="onMenuToggle()"
    >
      <i class="pi pi-bars"></i>
    </button>

    <button
      class="p-link layout-topbar-menu-button layout-topbar-button"
      @click="onTopBarMenuButton()"
    >
      <i class="pi pi-ellipsis-v"></i>
    </button>


    <div class="header-logo">
      <router-link to="/auction/category/All" class="layout-topbar-logo">
        <img src="@/assets/tpwf-transparent-no-padding.png" alt="logo" />
      </router-link>
    </div>

        <div class="layout-topbar-menu" :class="topbarMenuClasses">
      <button
        class="p-link layout-topbar-button help-bell"
        :class="{ 'has-open-requests': openHelpCount > 0 }"
        @click="helpDialogVisible = true"
      >
        <div class="help-bell-icon-wrap">
          <i class="pi pi-bell"></i>
          <span v-if="openHelpCount > 0" class="help-count">{{ openHelpCount }}</span>
        </div>
        <span>Hj&auml;lp</span>
      </button>

      <div v-if="isUserAuthenticated">
        <router-link :to="{ name: 'Edit User Info' }">
          <button
            class="p-link layout-topbar-button profile-user-button"
            @click="onSettingsClick()"
          >
            <i class="pi pi-user"></i>
            <p class="pb-0 pt-3 pl-2">{{ displayName }}</p>
          </button>
        </router-link>
      </div>

      <button
        v-if="isUserAuthenticated"
        class="p-link layout-topbar-button"
        @click="signOut"
      >
        <i class="pi pi-sign-out"></i>
        <span>Logga ut</span>
      </button>
    </div>

    <PrimeDialog v-model:visible="helpDialogVisible" modal header="Aktiva hj&auml;lp&auml;renden" :style="{ width: '42rem' }">
      <div v-if="openHelpRequests.length" class="help-list">
        <div
          v-for="request in openHelpRequests"
          :key="request.id"
          class="help-item"
        >
          <div class="help-item-head">
            <strong>{{ getRequestSummary(request) }}</strong>
            <small>{{ formatRequestTimestamp(request.createdAt) }}</small>
          </div>
          <div class="help-meta">
            <span class="badge bg-secondary">{{ request.type || 'General' }}</span>
          </div>
          <p class="help-message">{{ request.message }}</p>
          <button class="btn btn-sm btn-success" @click="markHelpResolved(request.id)">
            Markera som hanterad
          </button>
        </div>
      </div>
      <p v-else class="mb-0">Inga aktiva hj&auml;lp&auml;renden just nu.</p>
    </PrimeDialog>
  </div>
</template>

<style lang="scss" scoped>
.layout-topbar-button.profile-user-button {
  width: 220px;
}

.header-logo {
  align-items: center;
  justify-content: center;
  display: flex;
  flex: 1;
  min-width: 0;
}

.help-bell {
  position: relative;
}
.help-bell.has-open-requests .pi-bell {
  color: #dc2626;
}

.help-bell-icon-wrap {
  position: relative;
  display: inline-flex;
  align-items: center;
}

.help-count {
  position: absolute;
  top: -8px;
  right: -10px;
  background: #dc2626;
  color: #fff;
  border-radius: 999px;
  min-width: 18px;
  height: 18px;
  line-height: 18px;
  text-align: center;
  font-size: 0.7rem;
  padding: 0 4px;
}

.help-list {
  display: grid;
  gap: 0.75rem;
}

.help-item {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 0.75rem;
  text-align: left;
}

.help-item-head {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 0.5rem;
  margin-bottom: 0.4rem;
}

.help-meta {
  margin-bottom: 0.4rem;
}

.help-message {
  margin-bottom: 0.5rem;
  color: #374151;
}
</style>











